import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Empreendimento,
  ParteEmpreendimento,
  EmpreendimentoUpdate,
} from "../../types/empreendimento"; // Ajustado para EmpreendimentoUpdate, que é o tipo que o onSave espera
import { Incorporadora } from "../../types/incorporadora";
import { empreendimentoService } from "../../services/empreendimentoService";
import { incorporadoraService } from "../../services/incorporadoraService";
import EmpreendimentoEditForm from "./EmpreendimentoEditForm";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import Slider, { CustomArrowProps } from "react-slick";
import Tipologias from "./componentes/unidades";

// Serviços para dados locais
import { ItemEmpreendimentoService } from "../../services/empreendimentoService";

// --- COMPONENTES DE ARROW (MANTIDOS) ---
const NextArrow: React.FC<CustomArrowProps> = ({
  className,
  style,
  onClick,
}) => {
  return (
    <div
      className={`absolute right-2 top-1/2 transform -translate-y-1/2 z-10 cursor-pointer`}
      style={style}
      onClick={onClick}
    >
      <svg
        className="w-8 h-8 text-white shadow-lg"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
          clipRule="evenodd"
        />
      </svg>
    </div>
  );
};

const PrevArrow: React.FC<CustomArrowProps> = ({
  className,
  style,
  onClick,
}) => {
  return (
    <div
      className={`absolute left-2 top-1/2 transform -translate-y-1/2 z-10 cursor-pointer`}
      style={style}
      onClick={onClick}
    >
      <svg
        className="w-8 h-8 text-white shadow-lg"
        fill="currentColor"
        viewBox="0 0 20 20"
      >
        <path
          fillRule="evenodd"
          d="M12.707 14.707a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414l4-4a1 1 0 111.414 1.414L10.414 10l2.293 2.293a1 1 0 010 1.414z"
          clipRule="evenodd"
        />
      </svg>
    </div>
  );
};
// ------------------------------------------

const itemEmpreendimentoService = new ItemEmpreendimentoService();

const VisualizarEmpreendimento: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [empreendimento, setEmpreendimento] = useState<Empreendimento | null>(
    null
  );
  const [incorporadora, setIncorporadora] = useState<Incorporadora | null>(
    null
  );
  const [partesEmpreendimento, setPartesEmpreendimento] = useState<
    ParteEmpreendimento[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [exportando, setExportando] = useState(false);

  const handleExportarCsv = async () => {
    if (!empreendimento) return;
    setExportando(true);
    try {
      await empreendimentoService.exportarCsv(empreendimento.id);
    } catch (err) {
      console.error("Erro ao exportar CSV do empreendimento:", err);
      alert("Erro ao exportar o CSV do empreendimento");
    } finally {
      setExportando(false);
    }
  };

  // Estados para dados específicos (MANTIDOS)
  const [faseEmpreendimento, setFaseEmpreendimento] = useState<
    number | undefined
  >(undefined);
  const [tipoEmpreendimento, setTipoEmpreendimento] = useState<
    string[] | undefined
  >(undefined);
  const [itensEmpreendimento, setItensEmpreendimento] = useState<string[]>([]);

  // Função de carregamento principal (agora é a única lógica de 'GET')
  const carregarEmpreendimento = async (empreendimentoId: number) => {
    // Movida para escopo superior
    try {
      setLoading(true);
      setError(null);

      if (!empreendimentoId || isNaN(empreendimentoId)) {
        throw new Error("ID do empreendimento inválido");
      }

      // ... (Restante da lógica de carregamento do empreendimento, incorporadora e partes)

      let empreendimentoEncontrado: Empreendimento;
      try {
        empreendimentoEncontrado = await empreendimentoService.getById(
          empreendimentoId
        );
      } catch (getByIdError) {
        const listResponse = await empreendimentoService.list({ limit: 1000 });
        const found = listResponse.results.find(
          (emp) => emp.id === empreendimentoId
        );
        if (!found) {
          throw new Error(
            `Empreendimento com ID ${empreendimentoId} não encontrado`
          );
        }
        empreendimentoEncontrado = found;
      }

      setEmpreendimento(empreendimentoEncontrado);

      if (empreendimentoEncontrado.incorporadora) {
        try {
          const incorporadoraData = await incorporadoraService.getById(
            empreendimentoEncontrado.incorporadora
          );
          setIncorporadora(incorporadoraData);
        } catch (err) {
          console.error("Erro ao carregar incorporadora:", err);
        }
      }

      try {
        const partesData = await empreendimentoService.listPartesEmpreendimento(
          empreendimentoId
        );
        setPartesEmpreendimento(partesData);
      } catch (err) {
        console.error("Erro ao carregar partes do empreendimento:", err);
        setPartesEmpreendimento([]);
      }

      setFaseEmpreendimento(empreendimentoEncontrado.fase_atual);
      setTipoEmpreendimento(
        empreendimentoEncontrado.tipo_empreendimento_descricao
      );
      setItensEmpreendimento(
        empreendimentoEncontrado.itens_empreendimento_descricoes || []
      );
    } catch (err: any) {
      console.error("Erro ao carregar empreendimento:", err);
      setError(err.message || "Erro ao carregar empreendimento");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      carregarEmpreendimento(parseInt(id));
    }
  }, [id]);

  // 1. FUNÇÃO DE SUCESSO DO SALVAMENTO (handleUpdateSuccess)
  const handleUpdateSuccess = () => {
    setIsEditing(false); // Fecha o formulário
    // 🐛 CORRIGIDO: Recarrega os dados chamando o 'carregarEmpreendimento'
    if (empreendimento) {
      carregarEmpreendimento(empreendimento.id);
    } else if (id) {
      // Caso empreendimento ainda seja null (improvável, mas seguro)
      carregarEmpreendimento(parseInt(id));
    }
    alert("Dados salvos com sucesso! ✅");
  };

  // 2. FUNÇÃO DE SALVAMENTO DE DADOS BÁSICOS (handleSave)
  const handleSave = async (data: Partial<EmpreendimentoUpdate>) => {
    // 🐛 CORRIGIDO: Tipagem e remoção da redundância
    // 🐛 CORRIGIDO: Verifica se o empreendimento é nulo
    if (!empreendimento) {
      console.error("Empreendimento não encontrado para salvar!");
      return;
    }

    // Lógica de salvar...

    // Esta função apenas cuida do PATCH principal (dados básicos/específicos)
    await empreendimentoService.patch(empreendimento.id, data);

    // O EmpreendimentoEditForm chama o onUpdateSuccess após o onSave ser bem-sucedido
    // Portanto, não precisamos chamá-lo aqui.
    // Se a chamada acima for bem-sucedida, o fluxo no EditForm garante o fechamento e recarregamento.
  };

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  // 🐛 REMOVIDO: A declaração duplicada e simplificada de handleSave foi removida daqui (linhas 145-151 originais).
  // A lógica foi movida para o 'handleSave' acima.

  // --- FUNÇÕES DE FORMATAÇÃO (MANTIDAS) ---
  const formatarData = (data: string | undefined) => {
    if (!data) return "-";
    return new Date(data).toLocaleDateString("pt-BR");
  };

  const formatarMoeda = (valor: string | number | undefined) => {
    if (!valor) return "-";
    const num = typeof valor === "string" ? parseFloat(valor) : valor;
    return new Intl.NumberFormat("pt-BR", {
      style: "currency",
      currency: "BRL",
    }).format(num);
  };

  const formatarAreaComUnidade = (area: string | number | undefined) => {
    if (!area) return "-";
    const num = typeof area === "string" ? parseFloat(area) : area;
    return `${num.toFixed(2).replace(".", ",")} m²`;
  };

  const formatarFaseEmpreendimento = (fase: number | undefined) => {
    if (fase === undefined || fase === null) return "-";
    const fases = {
      1: "Planta",
      2: "Fundação",
      3: "Estrutura",
      4: "Acabamento",
      5: "Pronto",
    };
    return fases[fase as keyof typeof fases] || `Fase ${fase}`;
  };
  // ------------------------------------------

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error || !empreendimento) {
    // ... (Renderização de erro/não encontrado mantida)
    return (
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-red-50 border border-red-200 rounded-md p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg
                className="h-5 w-5 text-red-400"
                viewBox="0 0 20 20"
                fill="currentColor"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Erro ao carregar empreendimento
              </h3>
              <div className="mt-2 text-sm text-red-700">
                {error || "Empreendimento não encontrado."}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- RENDERIZAÇÃO DA TELA DE VISUALIZAÇÃO OU EDIÇÃO (MANTIDA) ---
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      {/* ... (código do header mantido) ... */}
      <div className="mb-8">
        <div className="flex justify-between items-start">
          <div className="flex-1">
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {empreendimento.nome}
            </h1>
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <svg
                  className="w-5 h-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                  />
                </svg>
                <span className="text-lg text-gray-600">
                  {incorporadora?.razao_social ||
                    `Incorporadora ${empreendimento.incorporadora}`}
                </span>
              </div>
              {empreendimento.endereco?.logradouro?.cidade && (
                <div className="flex items-center space-x-2">
                  <svg
                    className="w-5 h-5 text-gray-400"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  <span className="text-lg text-gray-600">
                    {empreendimento.endereco.logradouro.cidade.nome} -{" "}
                    {empreendimento.endereco.logradouro.cidade.uf.sigla}
                  </span>
                </div>
              )}
            </div>
          </div>
          <div className="flex space-x-3 ml-6">
            <button
              onClick={() => navigate("/empreendimentos")}
              className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            >
              <div className="flex items-center space-x-2">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M10 19l-7-7m0 0l7-7m-7 7h18"
                  />
                </svg>
                <span>Voltar</span>
              </div>
            </button>
            <button
              onClick={handleExportarCsv}
              disabled={exportando}
              title="Baixa um CSV com dados, unidades, movimentações e consolidações"
              className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <div className="flex items-center space-x-2">
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                  />
                </svg>
                <span>{exportando ? "Gerando..." : "Exportar CSV"}</span>
              </div>
            </button>
            {!isEditing ? (
              <button
                onClick={handleEdit}
                className="px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                    />
                  </svg>
                  <span>Editar</span>
                </div>
              </button>
            ) : (
              <button
                onClick={handleCancelEdit}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                  <span>Cancelar</span>
                </div>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modo de Edição */}
      {isEditing ? (
        <EmpreendimentoEditForm
          empreendimento={empreendimento}
          onSave={handleSave}
          onUpdateSuccess={handleUpdateSuccess} // Passando a função corrigida
          onCancel={handleCancelEdit}
          loading={loading}
        />
      ) : (
        <>
          {/* Imagens, Informações Básicas, Endereço e Características (código de visualização mantido) */}
          {/* ... (restante do JSX de visualização) ... */}

          {/* Imagem do Empreendimento */}
          {empreendimento.imagens && empreendimento.imagens.length > 0 && (
            <div className="mb-8 bg-gray-100 rounded-xl relative w-full max-w-4xl mx-auto">
              <Slider
                dots={true}
                infinite={true}
                speed={500}
                slidesToShow={1}
                slidesToScroll={1}
                adaptiveHeight={true}
                nextArrow={<NextArrow />}
                prevArrow={<PrevArrow />}
                className="w-full"
              >
                {empreendimento.imagens.map((img, index) => (
                  <div key={index} className="w-full">
                    <img
                      src={img}
                      alt={`Imagem ${index + 1} do empreendimento ${
                        empreendimento.nome
                      }`}
                      className="w-full h-auto max-h-[500px] object-cover rounded-xl"
                    />
                  </div>
                ))}
              </Slider>
            </div>
          )}

          {/* Informações Básicas */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900">
                Informações Básicas
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Detalhes fundamentais do empreendimento
              </p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Nome
                  </label>
                  <p className="text-lg font-medium text-gray-900">
                    {empreendimento.nome}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Incorporadora
                  </label>
                  <p className="text-lg font-medium text-gray-900">
                    {incorporadora?.razao_social || `-`}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Área Total
                  </label>
                  <p className="text-lg font-medium text-gray-900">
                    {formatarAreaComUnidade(empreendimento.area_total)}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Área Construída
                  </label>
                  <p className="text-lg font-medium text-gray-900">
                    {formatarAreaComUnidade(empreendimento.area_construida)}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Quantidade de Elevadores
                  </label>
                  <p className="text-lg font-medium text-gray-900">
                    {empreendimento.elevadores ?? "-"}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    É Lançamento?
                  </label>
                  <p className="text-lg font-medium text-gray-900">
                    {empreendimento.is_lancamento ? "Sim" : "Não"}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Data de Entrega
                  </label>
                  <p className="text-lg font-medium text-gray-900">
                    {formatarData(empreendimento.data_entrega)}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Início da Comercialização
                  </label>
                  <p className="text-lg font-medium text-gray-900">
                    {formatarData(empreendimento.data_inicio_comercializacao)}
                  </p>
                </div>

                {empreendimento.heading && (
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Heading
                    </label>
                    <p className="text-lg font-medium text-gray-900">
                      {empreendimento.heading}
                    </p>
                  </div>
                )}

                {empreendimento.pitch && (
                  <div className="space-y-2 md:col-span-2">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Pitch
                    </label>
                    <p className="text-base text-gray-800 italic">
                      "{empreendimento.pitch}"
                    </p>
                  </div>
                )}

                {empreendimento.observacao && (
                  <div className="space-y-2 md:col-span-3">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Observações
                    </label>
                    <p className="text-base text-gray-800">
                      {empreendimento.observacao}
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Documentos */}
          {(empreendimento.book || empreendimento.arquivo_extra_1 || empreendimento.arquivo_extra_2) && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
              <div className="px-6 py-5 border-b border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900">Documentos</h3>
                <p className="mt-1 text-sm text-gray-500">
                  Cada envio fica registrado nas movimentações do empreendimento, com data.
                </p>
              </div>
              <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-4">
                {empreendimento.book && (
                  <a
                    href={empreendimento.book}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center px-4 py-3 border border-gray-200 rounded-lg text-blue-600 hover:bg-blue-50 hover:border-blue-300 transition-colors font-medium"
                  >
                    Book
                  </a>
                )}
                {empreendimento.arquivo_extra_1 && (
                  <a
                    href={empreendimento.arquivo_extra_1}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center px-4 py-3 border border-gray-200 rounded-lg text-blue-600 hover:bg-blue-50 hover:border-blue-300 transition-colors font-medium"
                  >
                    Arquivo Extra 1
                  </a>
                )}
                {empreendimento.arquivo_extra_2 && (
                  <a
                    href={empreendimento.arquivo_extra_2}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center justify-center px-4 py-3 border border-gray-200 rounded-lg text-blue-600 hover:bg-blue-50 hover:border-blue-300 transition-colors font-medium"
                  >
                    Arquivo Extra 2
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Endereço */}
          {empreendimento.endereco && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
              <div className="px-6 py-5 border-b border-gray-100">
                <h3 className="text-xl font-semibold text-gray-900">
                  Endereço
                </h3>
                <p className="mt-1 text-sm text-gray-500">
                  Localização do empreendimento
                </p>
              </div>
              <div className="p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Logradouro
                    </label>
                    <p className="text-lg font-medium text-gray-900">
                      {empreendimento.endereco.logradouro?.nome || "-"}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Número
                    </label>
                    <p className="text-lg font-medium text-gray-900">
                      {empreendimento.endereco.numero || "-"}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Complemento
                    </label>
                    <p className="text-lg font-medium text-gray-900">
                      {empreendimento.endereco.complemento || "-"}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Bairro
                    </label>
                    <p className="text-lg font-medium text-gray-900">
                      {empreendimento.endereco.logradouro?.bairro?.nome || "-"}
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      Cidade/UF
                    </label>
                    <p className="text-lg font-medium text-gray-900">
                        {`${empreendimento.endereco.logradouro.cidade.nome} - ${empreendimento.endereco.logradouro.cidade.uf.sigla}`}
                    </p>                  </div>

                  <div className="space-y-2">
                    <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                      CEP
                    </label>
                    <p className="text-lg font-medium text-gray-900">
                      {empreendimento.endereco.logradouro?.zipcode || "-"}
                    </p>
                  </div>

                  {(empreendimento.endereco.latitude ||
                    empreendimento.endereco.longitude) && (
                    <div className="space-y-2 md:col-span-2">
                      <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                        Coordenadas
                      </label>
                      <p className="text-lg font-medium text-gray-900">
                        {empreendimento.endereco.latitude},{" "}
                        {empreendimento.endereco.longitude}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Informações Específicas */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900">
                Características e Itens
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Detalhes técnicos e comerciais
              </p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Fase do Empreendimento
                  </label>
                  <p className="text-lg font-medium text-gray-900">
                    {formatarFaseEmpreendimento(faseEmpreendimento)}
                  </p>
                </div>

                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Tipo do Empreendimento
                  </label>
                  <p className="text-lg font-medium text-gray-900">
                    {tipoEmpreendimento?.join(", ") || "Não informado"}
                  </p>
                </div>

                <div className="space-y-2 md:col-span-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Itens do Empreendimento
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {itensEmpreendimento && itensEmpreendimento.length > 0 ? (
                      itensEmpreendimento.map((item) => (
                        <span
                          key={item}
                          className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-gray-100 text-gray-800"
                        >
                          {item}
                        </span>
                      ))
                    ) : (
                      <p className="text-lg font-medium text-gray-900">
                        Não informado
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tipologias - Substituído pelo novo componente */}
          <Tipologias
            partesEmpreendimento={partesEmpreendimento}
            formatarAreaComUnidade={formatarAreaComUnidade}
            formatarMoeda={formatarMoeda}
          />

          {/* Metadados */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-8">
            <div className="px-6 py-5 border-b border-gray-100">
              <h3 className="text-xl font-semibold text-gray-900">
                Informações do Sistema
              </h3>
              <p className="mt-1 text-sm text-gray-500">
                Dados de criação e última modificação
              </p>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Criado por
                  </label>
                  <p className="text-lg font-medium text-gray-900">
                    {empreendimento.created_by || "-"}
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Modificado por
                  </label>
                  <p className="text-lg font-medium text-gray-900">
                    {empreendimento.modified_by || "-"}
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Data de Criação
                  </label>
                  <p className="text-lg font-medium text-gray-900">
                    {formatarData(empreendimento.created)}
                  </p>
                </div>
                <div className="space-y-2">
                  <label className="text-xs font-medium text-gray-500 uppercase tracking-wide">
                    Última Modificação
                  </label>
                  <p className="text-lg font-medium text-gray-900">
                    {formatarData(empreendimento.modified)}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default VisualizarEmpreendimento;
